/**
 * ConnectedDevices — the guitar-effects pedal registry (DEC-152), as its
 * own modal off the logo/Navigate menu.
 *
 * Moved out of Settings 2026-08-04 (operator ask): a pedal is project-scoped
 * rig truth, the same category as Timelines/Matrix — both of which already
 * live off the logo menu, not buried in a per-browser Settings preference
 * panel. This IS the whole story now: Settings no longer has its own copy.
 *
 * ## Add-by-model, not two hardcoded buttons
 *
 * `PEDAL_MODELS` in `layout/projectStore.ts` is the catalog — today "Line 6
 * Helix Floor" (auto-detects its port by name) and "Generic pedal" (no name
 * to search for, so its port is a one-time pick per project, inherited by
 * every later Generic pedal). A future model needing its own multi-message
 * protocol still needs real dispatcher code (DEC-148/152 sub-decision 4);
 * this catalog only removes the UI-side friction of listing a new model.
 *
 * ## Channel lives ONLY here
 *
 * 2026-08-04 operator ask: "user can assign the midi channel there [Connected
 * Devices] (keep the option non-changeable in the item inspector)". Items
 * bound to a pedal (`PresetChangeItem`/`SnapshotChangeItem`/pedal-bound
 * `ButtonItem`, all via `pedalId`) resolve port + channel LIVE from here —
 * change a pedal's channel once and every tile bound to it follows, with
 * nothing to re-author per item. See the `pedalId` doc comments in
 * `projectStore.ts` for the full reasoning.
 */

import { createSignal, For, Show, onMount, onCleanup } from 'solid-js';
import { bridges, getConnection, httpClientFor } from '../opbridge/store.ts';
import {
  activePedals, addPedal, updatePedal, removePedal, pedalConflict,
  pedalModel, PEDAL_MODELS, type PedalModel,
} from '../layout/projectStore.ts';
import { detectPortAcrossMesh } from '../pedals/detect.ts';
import type { OpbridgeHttpClient } from '../opbridge/httpClient.ts';
import { pedalBridgeRowId } from '../pedals/bridgeBinding.ts';

type ToastTone = 'ok' | 'err' | 'info';
interface Toast { tone: ToastTone; text: string }

export function ConnectedDevices() {
  const [toast, setToast] = createSignal<Toast | null>(null);
  function flash(tone: ToastTone, text: string): void {
    setToast({ tone, text });
    window.setTimeout(() => {
      setToast((t) => (t?.text === text ? null : t));
    }, 6000);
  }

  const [busy, setBusy] = createSignal(false);
  const [portList, setPortList] = createSignal<string[]>([]);
  const [portListError, setPortListError] = createSignal('');
  /** Which pedal's port `<select>` is open for a manual override — only
   *  reachable for a pedal whose model has no auto-detect, and only past
   *  the first one of its kind (see PEDAL_MODELS' doc comment). */
  const [editingPortId, setEditingPortId] = createSignal<string | null>(null);
  /** The "+ Add Device" flow expands into a model list rather than
   *  spawning a pedal immediately — data-driven from PEDAL_MODELS so a
   *  third model is a catalog row, not a new button. */
  const [pickingModel, setPickingModel] = createSignal(false);

  /** `bridgeId` picks which bridge's ports to list — a pedal's own
   *  affinity when editing an existing row, `undefined` (default bridge)
   *  when there's no pedal yet (a fresh add). Querying the WRONG bridge
   *  here would silently return another machine's port list — e.g.
   *  "Detect" for a Mac-bound Helix probing the dev PC instead. */
  /** Every currently-connected mesh bridge, default bridge first, paired
   *  with its live client — the scan order for cross-mesh auto-detect. */
  function meshCandidates(): { bridgeId: string; client: OpbridgeHttpClient }[] {
    const all = bridges();
    const ordered = [...all.filter((b) => b.is_primary), ...all.filter((b) => !b.is_primary)];
    const out: { bridgeId: string; client: OpbridgeHttpClient }[] = [];
    for (const b of ordered) {
      const conn = getConnection(b.id);
      if (conn?.status() === 'connected') out.push({ bridgeId: b.id, client: conn.httpClient() });
    }
    return out;
  }

  async function fetchPortList(bridgeId?: string): Promise<string[]> {
    const client = httpClientFor(bridgeId);
    if (!client) { setPortListError('No OPBridge is connected, so its MIDI ports cannot be listed.'); return []; }
    try {
      const { outputs } = await client.midiPorts();
      setPortListError('');
      setPortList(outputs);
      return outputs;
    } catch (err) {
      setPortListError(err instanceof Error ? err.message : String(err));
      return [];
    }
  }

  async function addPedalFromModel(model: PedalModel): Promise<void> {
    setPickingModel(false);
    setBusy(true);
    try {
      let port: string | undefined;
      let bridgeId: string | undefined;
      if (model.autoDetectSubstring) {
        // Named device (the Helix) — scan EVERY connected mesh bridge, not
        // just the default one, so both the port AND which physical
        // machine it's wired to resolve with zero operator choice
        // (2026-08-13 operator ask: "opbuttons should just work").
        const candidates = meshCandidates();
        if (candidates.length === 0) { flash('err', 'No OPBridge is connected, so the port cannot be found.'); return; }
        const match = await detectPortAcrossMesh(candidates, model.autoDetectSubstring);
        port = match?.port;
        bridgeId = match?.bridgeId;
      } else {
        // No name to search for — inherit an existing pedal of the SAME
        // model's port if one exists (several such pedals commonly share
        // one hardware MIDI interface); only the first ever needs a pick.
        // Bridge has the same "no name to search by" problem, so it stays
        // a manual pick below (the operator is already here adding the
        // device by hand — no extra step).
        await fetchPortList();
        port = activePedals().find((pd) => pd.modelId === model.id && pd.port)?.port;
      }
      const name = [model.brand, model.model].filter(Boolean).join(' ');
      const pedal = addPedal({
        name,
        modelId: model.id,
        type: model.type,
        port,
        bridgeId,
        channel: model.defaultChannel ?? 0,
      });
      if (model.autoDetectSubstring) {
        flash(
          port ? 'ok' : 'err',
          port
            ? `${name} added. Found its port, "${port}".`
            : `${name} added, but no matching port was found. Plug it in and retry.`,
        );
      } else {
        flash(
          'info',
          port
            ? `${name} added on "${port}". Set its channel below.`
            : `${name} added. Pick its port once below (every pedal of this kind after it uses the same one), and set its channel.`,
        );
      }
      const conflict = pedalConflict(pedal.port, pedal.channel, pedal.id);
      if (conflict) {
        flash('err', `"${pedal.name}" and "${conflict.name}" are both on port "${pedal.port}" channel ${pedal.channel + 1}: they will collide.`);
      }
    } finally {
      setBusy(false);
    }
  }

  function onPortOrChannelChange(id: string, patch: Partial<{ port: string | undefined; channel: number; bridgeId: string | undefined; bridgeNodeId: string | undefined }>): void {
    updatePedal(id, patch);
    const pedal = activePedals().find((pd) => pd.id === id);
    if (!pedal) return;
    const conflict = pedalConflict(pedal.port, pedal.channel, id);
    if (conflict) {
      flash('err', `"${pedal.name}" and "${conflict.name}" are both on port "${pedal.port}" channel ${pedal.channel + 1}: they will collide.`);
    }
  }

  function onKey(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    if (pickingModel()) setPickingModel(false);
  }
  onMount(() => window.addEventListener('keydown', onKey));
  onCleanup(() => window.removeEventListener('keydown', onKey));

  // The section's content, shared by its own window and its pane in Navigate
  // (2026-10-05, app-study.html: every section opens inside Navigate).
  const body = () => (
    <>
          <section class="nv-ss">
          <div class="nv-ss-h">
            <h2>Your pedals</h2>
            <p>Each pedal once: its name, port and channel live here, plugged in or not. Two pedals on the same port AND channel collide; different ports never do.</p>
          </div>
          <Show when={activePedals().length > 0} fallback={<p class="nav-empty">No pedals yet. Add one below.</p>}>
          <div class="nav-well">
          <For each={activePedals()}>
            {(pedal) => {
              const conflict = () => pedalConflict(pedal.port, pedal.channel, pedal.id);
              const model = () => pedalModel(pedal.modelId);
              return (
                <div class="nav-lr guitar-pedal-row" data-conflict={conflict() ? 'true' : 'false'}>
                  <span class="guitar-pedal-fields">
                    <span class="nv-fld guitar-pedal-name-fld">
                      <input
                        type="text"
                        class="guitar-pedal-name"
                        aria-label="Pedal name"
                        value={pedal.name}
                        onChange={(e) => updatePedal(pedal.id, { name: e.currentTarget.value.trim() || pedal.name })}
                      />
                    </span>
                    <span class="nv-ct guitar-pedal-type">
                      {model()?.model ?? (pedal.type === 'helix' ? 'Helix' : 'Generic')}
                    </span>

                    <Show
                      when={!model()?.autoDetectSubstring}
                      fallback={
                        <>
                          <span class="guitar-pedal-port" data-found={pedal.port ? 'true' : 'false'}>
                            {pedal.port ?? 'Not found: plug it in'}
                          </span>
                          <button
                            type="button"
                            class="nv-b nv-b-sm"
                            disabled={busy()}
                            onClick={async () => {
                              const substring = model()?.autoDetectSubstring;
                              if (!substring) return;
                              // Cross-mesh, not just this pedal's current
                              // bridge — the device may have moved to a
                              // different machine since it was last found.
                              const candidates = meshCandidates();
                              if (candidates.length === 0) { flash('err', 'No connected OPBridge.'); return; }
                              const match = await detectPortAcrossMesh(candidates, substring);
                              onPortOrChannelChange(pedal.id, {
                                port: match?.port,
                                bridgeId: match?.bridgeId,
                                bridgeNodeId: match
                                  ? bridges().find((b) => b.id === match.bridgeId)?.nodeId ?? undefined
                                  : undefined,
                              });
                              flash(match ? 'ok' : 'err', match ? `Found "${match.port}".` : 'Still not found.');
                            }}
                          >
                            {pedal.port ? 'Re-detect' : 'Detect'}
                          </button>
                        </>
                      }
                    >
                      {/* No auto-detect for this model — port is a manual
                          pick, only ever surfaced for the first pedal of
                          its kind (see PEDAL_MODELS' doc comment); every
                          later one renders read-only + "Change" like the
                          auto-detected branch above. */}
                      <Show
                        when={pedal.port && editingPortId() !== pedal.id}
                        fallback={
                          <select
                            class="nv-select guitar-pedal-port"
                            aria-label="Port"
                            value={pedal.port ?? ''}
                            onChange={(e) => {
                              onPortOrChannelChange(pedal.id, { port: e.currentTarget.value || undefined });
                              setEditingPortId(null);
                            }}
                          >
                            <option value="">Pick a port</option>
                            <For each={portList()}>{(p) => <option value={p}>{p}</option>}</For>
                            <Show when={pedal.port && !portList().includes(pedal.port)}>
                              <option value={pedal.port}>{pedal.port}</option>
                            </Show>
                          </select>
                        }
                      >
                        <span class="guitar-pedal-port" data-found="true">{pedal.port}</span>
                        <button
                          type="button"
                          class="nv-b nv-b-sm"
                          disabled={busy()}
                          onClick={async () => {
                            await fetchPortList(pedal.bridgeId);
                            setEditingPortId(pedal.id);
                          }}
                        >
                          Change
                        </button>
                      </Show>
                    </Show>

                    <label class="nv-fld nv-fld-num guitar-pedal-channel">
                      <span class="nv-unit">Ch</span>
                      <input
                        aria-label="MIDI channel"
                        type="number" min="1" max="16"
                        value={pedal.channel + 1}
                        onChange={(e) => {
                          const ch = Math.min(16, Math.max(1, parseInt(e.currentTarget.value, 10) || 1)) - 1;
                          onPortOrChannelChange(pedal.id, { channel: ch });
                        }}
                      />
                    </label>

                    {/* Which mesh OPBridge this device is physically wired
                        to (per-item bridge affinity). A named auto-detect
                        model (the Helix) resolves this itself by scanning
                        every bridge for its port — 2026-08-13 operator ask:
                        "opbuttons should just work", no picker for it. Only
                        a nameless Generic pedal (no way to auto-detect
                        which machine it's on) gets a manual pick - and only
                        once a second bridge exists, since a picker with one
                        option is noise. */}
                    <Show when={!model()?.autoDetectSubstring && bridges().length > 1}>
                      <select
                        class="nv-select guitar-pedal-bridge"
                        aria-label="Computer"
                        value={pedalBridgeRowId(pedal) ?? ''}
                        onChange={(e) => {
                          // The option VALUES are local row ids (that is what
                          // the operator is looking at), but what gets STORED
                          // is the row's stable nodeId - a row id is
                          // meaningless on any other machine and this object
                          // rides the synced project document.
                          const rowId = e.currentTarget.value;
                          if (!rowId) {
                            updatePedal(pedal.id, { bridgeId: undefined, bridgeNodeId: undefined });
                            return;
                          }
                          const row = bridges().find((b) => b.id === rowId);
                          updatePedal(pedal.id, {
                            bridgeId: rowId,
                            // No node yet means /health has not backfilled this
                            // row. Storing nothing keeps the legacy binding
                            // working locally rather than writing a node we
                            // would have had to invent.
                            ...(row?.nodeId ? { bridgeNodeId: row.nodeId } : {}),
                          });
                        }}
                      >
                        {/* Empty value = "follow the default bridge" — tracks
                            whichever row is is_primary dynamically, NOT
                            pinned to its current id (distinct from
                            explicitly selecting that same bridge below). */}
                        <option value="">
                          Default ({bridges().find((b) => b.is_primary)?.bridge_instance_name
                            || bridges().find((b) => b.is_primary)?.name
                            || 'not set'})
                        </option>
                        <For each={bridges()}>
                          {(b) => <option value={b.id}>{b.bridge_instance_name || b.name}</option>}
                        </For>
                      </select>
                    </Show>
                  </span>
                  <button type="button" class="nv-b nv-b-q nv-b-sm" data-danger="true" onClick={() => removePedal(pedal.id)}>
                    Remove
                  </button>
                  <Show when={conflict()}>
                    <small class="guitar-pedal-conflict">
                      Same port and channel as "{conflict()?.name}": they will collide.
                    </small>
                  </Show>
                </div>
              );
            }}
          </For>
          </div>
          </Show>

          <Show when={portListError()}>
            <p class="nv-note guitar-pedal-conflict" data-tone="warn"><span>{portListError()}</span></p>
          </Show>

          <Show
            when={pickingModel()}
            fallback={
              <div class="nv-row">
                <button type="button" class="nv-b nv-b-pri" disabled={busy()} onClick={() => setPickingModel(true)}>
                  Add a pedal
                </button>
              </div>
            }
          >
            <div class="nv-ss-h"><h2>Which pedal?</h2></div>
            <ul class="nav-well backup-picker-list" data-nav-layer>
              <For each={PEDAL_MODELS}>
                {(model) => (
                  <li>
                    <button
                      type="button"
                      class="nav-lr nav-lr-button backup-picker-row"
                      disabled={busy()}
                      onClick={() => void addPedalFromModel(model)}
                    >
                      <span class="nav-lr-main">
                        <b class="backup-picker-row-stamp">{[model.brand, model.model].filter(Boolean).join(' ')}</b>
                        <small class="backup-picker-row-meta">{model.autoDetectSubstring ? 'Its port is found by itself' : 'You pick its port once'}</small>
                      </span>
                      <span class="nav-lr-chev" aria-hidden="true">›</span>
                    </button>
                  </li>
                )}
              </For>
            </ul>
            <div class="nv-row"><button type="button" class="nv-b nv-b-q" onClick={() => setPickingModel(false)}>Cancel</button></div>
          </Show>
          </section>

          <Show when={toast() !== null}>
            <div class="nv-note nv-toast" data-tone={toast()!.tone === 'err' ? 'warn' : 'info'}>
              <span>{toast()!.text}</span>
            </div>
          </Show>
    </>
  );
  return <div class="nav-pane-body" role="region" aria-label="Pedals and devices">{body()}</div>;
}
